import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BIKE = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#EE3B36" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18.5" cy="17.5" r="3.5"/><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="15" cy="5" r="1"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/></svg>';

const ICONS = {
  pickup: () => L.divIcon({
    className: '',
    html: '<span style="display:block;width:18px;height:18px;border-radius:9999px;background:#22A45D;border:4px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></span>',
    iconSize: [18, 18], iconAnchor: [9, 9],
  }),
  dropoff: () => L.divIcon({
    className: '',
    html: '<svg viewBox="0 0 24 24" width="32" height="32" fill="#EE3B36" stroke="#fff" stroke-width="1.5" style="filter:drop-shadow(0 2px 4px rgba(0,0,0,.35))"><path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"/></svg>',
    iconSize: [32, 32], iconAnchor: [16, 30],
  }),
  driver: () => L.divIcon({
    className: '',
    html: `<span style="display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:9999px;background:#fff;box-shadow:0 4px 12px rgba(0,0,0,.3)">${BIKE}</span>`,
    iconSize: [36, 36], iconAnchor: [18, 18],
  }),
  job: () => L.divIcon({
    className: '',
    html: '<span style="display:block;width:16px;height:16px;border-radius:9999px;background:#2F80ED;border:4px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></span>',
    iconSize: [16, 16], iconAnchor: [8, 8],
  }),
};

const Fitter = ({ points, follow }) => {
  const map = useMap();
  const key = points.map((p) => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`).join('|');
  useEffect(() => {
    if (!points.length) return;
    const d = points.find((p) => p.kind === 'driver');
    if (follow && d) { map.panTo([d.lat, d.lng], { animate: true }); return; }
    if (points.length === 1) { map.setView([points[0].lat, points[0].lng], 15); return; }
    map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])).pad(0.35), { animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, follow, map]);
  useEffect(() => { const t = setTimeout(() => map.invalidateSize(), 250); return () => clearTimeout(t); }, [map]);
  return null;
};

const LiveMap = ({ points = [], route = true, follow = false, className = '', children, zoom = 14 }) => {
  const valid = useMemo(() => points.filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng)), [points]);
  const center = valid[0] || { lat: 15.6566, lng: -86.0055 };
  const line = useMemo(() => {
    if (!route) return [];
    const pickup = valid.find((p) => p.kind === 'pickup');
    const driver = valid.find((p) => p.kind === 'driver');
    const dropoff = valid.find((p) => p.kind === 'dropoff');
    return [pickup, driver, dropoff].filter(Boolean).map((p) => [p.lat, p.lng]);
  }, [valid, route]);

  return (
    <div className={`relative z-0 isolate overflow-hidden ${className}`} data-testid="live-map">
      <MapContainer center={[center.lat, center.lng]} zoom={zoom} scrollWheelZoom={false} zoomControl={false} attributionControl={false} style={{ width: '100%', height: '100%' }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" maxZoom={19} />
        {line.length > 1 && <Polyline positions={line} pathOptions={{ color: '#2F80ED', weight: 5, opacity: 0.85 }} />}
        {valid.map((p, i) => <Marker key={p.id || `${p.kind}-${i}`} position={[p.lat, p.lng]} icon={(ICONS[p.kind] || ICONS.job)()} />)}
        <Fitter points={valid} follow={follow} />
      </MapContainer>
      <span className="absolute bottom-0 right-0 z-[400] bg-white/75 text-[9px] text-gray-600 px-1.5 rounded-tl">&copy; OpenStreetMap</span>
      {children}
    </div>
  );
};

export default LiveMap;
